/* eslint-disable import/order */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import * as z from 'zod';
import {
  Button,
  CircularProgress,
  Stack,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Typography,
  FormLabel,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { Add, Delete, Person, Schedule, Group, PersonAdd } from '@mui/icons-material';

import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreateReunion,
  useCreateReunionAuto,
  useUpdateReunion,
  useUpdateReunionEstado,
  esReunionApoyoOCounseling,
} from '../../hooks/useReuniones';
import AgendarSiguienteReunionModal from './AgendarSiguienteReunionModal';
import { Reunion } from '../../types/reuniones';

/* ✨ Staff, alumnos y CENTROS desde hooks */
import { useUsuarios } from '../../hooks/useUsuarios';
import { useClientes } from '../../hooks/useClientes';
import { useCentros } from '../../hooks/useCentros';

/* ─────── Constantes y tipos ─────── */
import {
  REUNION_TIPO_VALUES,
  REUNION_ESTADO_VALUES,
  REUNION_CANAL_VALUES,
  REUNION_TIPO_ASISTENTES_VALUES,
  REUNION_ORIGEN_VALUES,
  REUNION_TIPO_EVENTO_REFERENCIA_VALUES,
  REGLA_ROL_STAFF_DESTINO_VALUES,
  REUNION_PARTICIPANTE_ROL_VALUES,
  REUNION_PARTICIPANTE_ESTADO_VALUES,
} from '../../utils/reuniones';

type ReunionTipo = (typeof REUNION_TIPO_VALUES)[number];
type ReunionEstado = (typeof REUNION_ESTADO_VALUES)[number];
type ReunionCanal = (typeof REUNION_CANAL_VALUES)[number];
type ReunionTipoAsistentes = (typeof REUNION_TIPO_ASISTENTES_VALUES)[number];
type ReunionOrigen = (typeof REUNION_ORIGEN_VALUES)[number];
type ReunionTipoEventoReferencia = (typeof REUNION_TIPO_EVENTO_REFERENCIA_VALUES)[number];
type ReglaRolStaffDestino = (typeof REGLA_ROL_STAFF_DESTINO_VALUES)[number];
type ReunionParticipanteRol = (typeof REUNION_PARTICIPANTE_ROL_VALUES)[number];
type ReunionParticipanteEstado = (typeof REUNION_PARTICIPANTE_ESTADO_VALUES)[number];

interface Props {
  initialValues?: Partial<CreateReunionFormData> & {
    id?: number;
    participantes?: any[];
    idStaff?: number;
    idAlumno?: number;
  };
  onSaved?: () => void;
  onCancel?: () => void;
  autoSchedule?: boolean;
}

/* ─────── Esquema de validación ─────── */
const toNumber = (v: unknown) =>
  v === '' || v === undefined ? undefined : typeof v === 'string' ? Number(v) : v;

// Esquema para un participante
const participanteSchema = z.object({
  idUsuario: z.number().min(1, 'Usuario requerido'),
  rol: z.enum(
    REUNION_PARTICIPANTE_ROL_VALUES as [ReunionParticipanteRol, ...ReunionParticipanteRol[]],
  ),
  estadoParticipacion: z
    .enum(
      REUNION_PARTICIPANTE_ESTADO_VALUES as [
        ReunionParticipanteEstado,
        ...ReunionParticipanteEstado[],
      ],
    )
    .optional()
    .default('pendiente'),
  requerido: z.boolean().optional().default(true),
  notas: z.string().optional(),
});

// VALIDACIÓN: Debe haber exactamente UN staff_principal
const validateStaffPrincipal = (participantes: any[]) => {
  const staffPrincipales = participantes.filter((p) => p.rol === 'staff_principal');
  return staffPrincipales.length === 1;
};

// VALIDACIÓN: Máximo UN alumno_principal
const validateAlumnoPrincipal = (participantes: any[]) => {
  const alumnosPrincipales = participantes.filter((p) => p.rol === 'alumno_principal');
  return alumnosPrincipales.length <= 1;
};

// VALIDACIÓN: No duplicados de usuario
const validateNoDuplicates = (participantes: any[]) => {
  const ids = participantes.map((p) => p.idUsuario);
  return new Set(ids).size === ids.length;
};

const baseSchema = z.object({
  // Centro (opcional)
  idCentro: z.preprocess(toNumber, z.number().optional()),

  // Reserva origen (opcional)
  idReservaOrigen: z.preprocess(toNumber, z.number().optional()),

  // Tipo y asistentes
  tipo: z.enum(REUNION_TIPO_VALUES as [ReunionTipo, ...ReunionTipo[]]),
  tipoAsistentes: z.enum(
    REUNION_TIPO_ASISTENTES_VALUES as [ReunionTipoAsistentes, ...ReunionTipoAsistentes[]],
  ),

  // Canal
  canal: z.enum(REUNION_CANAL_VALUES as [ReunionCanal, ...ReunionCanal[]]),

  // Fechas y horas
  fecha: z.string().min(1, 'Fecha requerida'),
  horaInicio: z.string().regex(/^[0-9]{2}:[0-9]{2}$/, 'Formato HH:mm requerido'),
  horaFin: z.string().regex(/^[0-9]{2}:[0-9]{2}$/, 'Formato HH:mm requerido'),

  // Estado y origen
  estado: z.enum(REUNION_ESTADO_VALUES as [ReunionEstado, ...ReunionEstado[]]).optional(),
  origen: z.enum(REUNION_ORIGEN_VALUES as [ReunionOrigen, ...ReunionOrigen[]]).optional(),

  // Comentarios
  comentariosInternos: z.string().optional(),
  comentariosPublicos: z.string().optional(),

  // Evento de referencia
  fechaEventoReferencia: z.string().optional(),
  tipoEventoReferencia: z
    .enum(
      REUNION_TIPO_EVENTO_REFERENCIA_VALUES as [
        ReunionTipoEventoReferencia,
        ...ReunionTipoEventoReferencia[],
      ],
    )
    .optional(),

  // ⭐⭐ NUEVA ESTRUCTURA: Todos los participantes en un array
  participantes: z
    .array(participanteSchema)
    .min(1, 'Debe haber al menos un participante (staff principal)')
    .refine(validateStaffPrincipal, {
      message: 'Debe haber exactamente un staff principal',
    })
    .refine(validateAlumnoPrincipal, {
      message: 'Solo puede haber un alumno principal como máximo',
    })
    .refine(validateNoDuplicates, {
      message: 'No puede haber usuarios duplicados',
    }),
});

const autoSchema = baseSchema.extend({
  autoSchedule: z.literal(true),
  rolStaffDestino: z.enum(
    REGLA_ROL_STAFF_DESTINO_VALUES as [ReglaRolStaffDestino, ...ReglaRolStaffDestino[]],
  ),
  idStaffFijo: z.preprocess(toNumber, z.number().optional()),
  // ⭐⭐ CRÍTICO: En modo auto, el backend espera tipoReunion, no tipo
  tipoReunion: z.enum(REUNION_TIPO_VALUES as [ReunionTipo, ...ReunionTipo[]]),
});

const manualSchema = baseSchema.extend({
  autoSchedule: z.literal(false),
});

const schema = z.discriminatedUnion('autoSchedule', [autoSchema, manualSchema]);

type CreateReunionFormData = z.infer<typeof schema>;

/* ─────── Componente principal ─────── */
export default function ReunionForm({
  initialValues = {},
  onSaved,
  onCancel,
  autoSchedule = false,
}: Props) {
  const { t } = useTranslation<'translation'>();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Determinar si estamos en modo edición
  const isEditMode = !!initialValues.id;

  // Mutaciones
  const createReunionMutation = useCreateReunion();
  const createReunionAutoMutation = useCreateReunionAuto();
  const updateReunionMutation = useUpdateReunion();
  const updateReunionEstadoMutation = useUpdateReunionEstado();

  // Estado para el modal de agendar siguiente reunión
  const [modalSiguienteReunion, setModalSiguienteReunion] = useState<{
    open: boolean;
    reunion: any | null;
    fechaSugerida: string | null;
  }>({
    open: false,
    reunion: null,
    fechaSugerida: null,
  });

  /* ✨ Staff, alumnos y CENTROS desde hooks */
  const { rows: usuarios = [], loading: loadingUsuarios } = useUsuarios({ soloActivos: true });

  const staffList = useMemo(
    () =>
      (usuarios as any[]).filter((u) => {
        const rol = String((u as any).rol ?? '').toLowerCase();
        return rol !== 'profesor' && rol !== 'consultor' && rol !== 'cliente';
      }),
    [usuarios],
  );

  const { rows: alumnosList = [], loading: loadingAlumnos } = useClientes();
  const { rows: centrosList = [], loading: loadingCentros } = useCentros();

  // Solo reservas vía API directa
  const [reservasList, setReservasList] = useState<any[]>([]);
  const [loadingExtra, setLoadingExtra] = useState(true);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [participantesCargados, setParticipantesCargados] = useState(false);
  const [participantesDesdeAPI, setParticipantesDesdeAPI] = useState<any[]>([]);

  /* ── Cargar solo reservas ── */
  useEffect(() => {
    const hoy = dayjs().startOf('day');
    const loadData = async () => {
      try {
        const reservasRes = await api.get('/reservas', {
          params: {
            limit: 100,
            estado: 'confirmed',
            desde: hoy.subtract(3, 'month').toISOString(),
            hasta: hoy.add(3, 'month').toISOString(),
          },
        });

        const reservasData = reservasRes.data;
        const reservasArray = Array.isArray(reservasData)
          ? reservasData
          : Array.isArray((reservasData as any).rows)
            ? (reservasData as any).rows
            : [];

        setReservasList(reservasArray);
      } catch (error) {
        enqueueSnackbar('Error cargando reservas', { variant: 'error' });
      } finally {
        setLoadingExtra(false);
      }
    };

    loadData();
  }, [enqueueSnackbar]);

  // Función para cargar participantes de la reunión (solo en modo edición)
  const fetchParticipantesReunion = useCallback(async (reunionId: number) => {
    try {
      const { data } = await api.get(`/reuniones/${reunionId}/participantes`);
      return data;
    } catch (error) {
      throw error;
    }
  }, []);

  const isLoading = loadingUsuarios || loadingAlumnos || loadingCentros || loadingExtra;

  /* ── Preparar participantes iniciales ── */
  const prepararParticipantesIniciales = useCallback(() => {
    // Primero, ver si ya tenemos participantes cargados desde la API
    if (participantesDesdeAPI.length > 0) {
      return participantesDesdeAPI;
    }

    const participantes = initialValues.participantes || [];

    // Si hay participantes en initialValues, usarlos
    if (participantes.length > 0) {
      return participantes.map((participante) => ({
        idUsuario: participante.idUsuario || participante.idUsuario || 0,
        rol: participante.rol || 'invitado',
        estadoParticipacion: participante.estadoParticipacion || 'pendiente',
        requerido: participante.requerido !== undefined ? participante.requerido : true,
        notas: participante.notas || '',
      }));
    }

    // Si no hay participantes en initialValues, crear desde idStaff e idAlumno
    const nuevosParticipantes = [];

    // Staff principal
    if (initialValues.idStaff) {
      nuevosParticipantes.push({
        idUsuario: initialValues.idStaff,
        rol: 'staff_principal' as const,
        estadoParticipacion: 'pendiente' as const,
        requerido: true,
        notas: '',
      });
    } else if (user?.id) {
      nuevosParticipantes.push({
        idUsuario: user.id,
        rol: 'staff_principal' as const,
        estadoParticipacion: 'pendiente' as const,
        requerido: true,
        notas: '',
      });
    }

    // Alumno principal
    if (initialValues.idAlumno) {
      nuevosParticipantes.push({
        idUsuario: initialValues.idAlumno,
        rol: 'alumno_principal' as const,
        estadoParticipacion: 'pendiente' as const,
        requerido: true,
        notas: '',
      });
    }

    return nuevosParticipantes;
  }, [initialValues, user, participantesDesdeAPI]);

  /* ── Configurar formulario ── */
  const defaultValues = useMemo(() => {
    const isEdit = !!initialValues.id;
    const effectiveAutoSchedule = isEdit ? false : autoSchedule;

    const participantesIniciales = prepararParticipantesIniciales();

    const base = {
      idCentro: initialValues.idCentro,
      idReservaOrigen: initialValues.idReservaOrigen,
      tipo: initialValues.tipo || 'seguimiento',
      tipoAsistentes: initialValues.tipoAsistentes || 'tutores_y_alumno',
      canal: initialValues.canal || 'online',
      fecha: initialValues.fecha || dayjs().format('YYYY-MM-DD'),
      horaInicio: initialValues.horaInicio || '10:00',
      horaFin: initialValues.horaFin || '11:00',
      estado: initialValues.estado || 'programada',
      origen: initialValues.origen || 'manual',
      comentariosInternos: initialValues.comentariosInternos || '',
      comentariosPublicos: initialValues.comentariosPublicos || '',
      fechaEventoReferencia: initialValues.fechaEventoReferencia || '',
      tipoEventoReferencia: initialValues.tipoEventoReferencia,
      participantes: participantesIniciales,
    };

    if (effectiveAutoSchedule && !isEdit) {
      return {
        ...base,
        autoSchedule: true as const,
        rolStaffDestino: (initialValues as any).rolStaffDestino || 'att_familias',
        idStaffFijo: (initialValues as any).idStaffFijo,
        tipoReunion: initialValues.tipo || 'seguimiento',
      };
    } else {
      return {
        ...base,
        autoSchedule: false as const,
      };
    }
  }, [autoSchedule, initialValues, prepararParticipantesIniciales]);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    watch,
    setValue,
    trigger,
    reset,
  } = useForm<CreateReunionFormData>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  // Cargar participantes cuando esté en modo edición
  useEffect(() => {
    const cargarParticipantes = async () => {
      if (isEditMode && initialValues.id && !participantesCargados) {
        try {
          const participantesData = await fetchParticipantesReunion(initialValues.id);

          if (participantesData && participantesData.length > 0) {
            // Mapear los participantes al formato esperado por el formulario
            const participantesFormateados = participantesData.map((participante: any) => ({
              idUsuario: participante.idUsuario || participante.id || 0,
              rol: participante.rol || 'invitado',
              estadoParticipacion: participante.estadoParticipacion || 'pendiente',
              requerido: participante.requerido !== undefined ? participante.requerido : true,
              notas: participante.notas || '',
            }));

            // Guardar los participantes de la API para usarlos en prepararParticipantesIniciales
            setParticipantesDesdeAPI(participantesFormateados);

            // Actualizar el valor del campo participantes
            setValue('participantes', participantesFormateados);
            setParticipantesCargados(true);

            // Si no hay staff principal en los participantes de la API, asegurarse de que haya uno
            const tieneStaffPrincipal = participantesFormateados.some(
              (p: { rol: string }) => p.rol === 'staff_principal',
            );
            if (!tieneStaffPrincipal && user?.id) {
              // Agregar al usuario actual como staff principal si no hay ninguno
              setValue('participantes', [
                {
                  idUsuario: user.id,
                  rol: 'staff_principal' as const,
                  estadoParticipacion: 'pendiente' as const,
                  requerido: true,
                  notas: '',
                },
                ...participantesFormateados,
              ]);
            }
          }
        } catch (error) {
          enqueueSnackbar('Error cargando participantes de la reunión', { variant: 'warning' });
        }
      }
    };

    cargarParticipantes();
  }, [
    isEditMode,
    initialValues.id,
    fetchParticipantesReunion,
    setValue,
    enqueueSnackbar,
    participantesCargados,
    user,
  ]);

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'participantes',
  });

  const isAutoSchedule = watch('autoSchedule');
  const fecha = watch('fecha');
  const horaInicio = watch('horaInicio');
  const horaFin = watch('horaFin');
  const participantes = watch('participantes');

  // Resetear el formulario cuando se carguen los participantes
  useEffect(() => {
    if (isEditMode && participantesCargados) {
      // Forzar una validación después de cargar los participantes
      trigger();
    }
  }, [isEditMode, participantesCargados, trigger]);

  // Obtener staff principal y alumno principal
  const staffPrincipal = useMemo(() => {
    return participantes.find((p) => p.rol === 'staff_principal');
  }, [participantes]);

  const alumnoPrincipal = useMemo(() => {
    return participantes.find((p) => p.rol === 'alumno_principal');
  }, [participantes]);

  const participantesSecundarios = useMemo(() => {
    return participantes.filter(
      (p) => p.rol === 'staff_secundario' || p.rol === 'alumno_secundario' || p.rol === 'invitado',
    );
  }, [participantes]);

  /* ── Validar horas y fechas ── */
  useEffect(() => {
    if (horaInicio && horaFin) {
      const inicio = dayjs(`2000-01-01 ${horaInicio}`);
      const fin = dayjs(`2000-01-01 ${horaFin}`);

      if (fin.isBefore(inicio) || fin.isSame(inicio)) {
        setValue('horaFin', dayjs(inicio).add(30, 'minutes').format('HH:mm'));
      }
    }
  }, [horaInicio, horaFin, setValue]);

  /* ── Validar que no sea en el pasado ── */
  const fechaInicioCompleta = useMemo(() => {
    if (fecha && horaInicio) {
      return dayjs(`${fecha} ${horaInicio}`);
    }
    return null;
  }, [fecha, horaInicio]);

  const fechaFinCompleta = useMemo(() => {
    if (fecha && horaFin) {
      return dayjs(`${fecha} ${horaFin}`);
    }
    return null;
  }, [fecha, horaFin]);

  const duracionMinutos = useMemo(() => {
    if (fechaInicioCompleta && fechaFinCompleta) {
      return fechaFinCompleta.diff(fechaInicioCompleta, 'minutes');
    }
    return 0;
  }, [fechaInicioCompleta, fechaFinCompleta]);

  /* ── Verificar disponibilidad ── */
  const checkAvailability = useCallback(async () => {
    if (!fechaInicioCompleta || !fechaFinCompleta || !staffPrincipal) {
      return { available: true, message: '' };
    }

    setCheckingAvailability(true);
    try {
      // Verificar solapamiento con otras reuniones del staff principal
      const idStaffPrincipal = staffPrincipal.idUsuario;
      const reunionesRes = await api.get('/reuniones', {
        params: {
          idStaff: idStaffPrincipal,
          fechaDesde: fechaInicioCompleta.subtract(1, 'day').toISOString(),
          fechaHasta: fechaInicioCompleta.add(1, 'day').toISOString(),
        },
      });

      const reunionesExistentes = reunionesRes.data || [];
      const solapamiento = reunionesExistentes.some((reunion: any) => {
        if (isEditMode && reunion.id === initialValues.id) return false;

        const inicioExistente = dayjs(reunion.fechaInicio);
        const finExistente = dayjs(reunion.fechaFin);

        return !(
          finExistente.isBefore(fechaInicioCompleta) || inicioExistente.isAfter(fechaFinCompleta)
        );
      });

      if (solapamiento) {
        return {
          available: false,
          message: 'El staff principal tiene otra reunión en este horario',
        };
      }

      // Verificar solapamiento con reservas del staff principal (como profesor)
      if (idStaffPrincipal) {
        const reservasRes = await api.get('/reservas', {
          params: {
            idProfesor: idStaffPrincipal,
            fecha: fechaInicioCompleta.format('YYYY-MM-DD'),
          },
        });

        const reservas = reservasRes.data?.rows || reservasRes.data || [];
        const solapamientoReserva = reservas.some((reserva: any) => {
          const inicioReserva = dayjs(`${reserva.fecha} ${reserva.horaInicio}`);
          const finReserva = dayjs(`${reserva.fecha} ${reserva.horaFin}`);

          return !(
            finReserva.isBefore(fechaInicioCompleta) || inicioReserva.isAfter(fechaFinCompleta)
          );
        });

        if (solapamientoReserva) {
          return {
            available: false,
            message: 'El staff principal tiene una clase en este horario',
          };
        }
      }

      return { available: true, message: '' };
    } catch (error) {
      return { available: true, message: '' };
    } finally {
      setCheckingAvailability(false);
    }
  }, [fechaInicioCompleta, fechaFinCompleta, staffPrincipal, isEditMode, initialValues.id]);

  /* ── Handler de envío ── */
  const onSubmit = useCallback(
    async (data: CreateReunionFormData) => {
      console.log('onSubmit llamado con datos:', data);
      try {
        // Validar que no sea en el pasado
        const inicio = dayjs(`${data.fecha} ${data.horaInicio}`);
        if (inicio.isBefore(dayjs())) {
          enqueueSnackbar('No se pueden crear reuniones en el pasado', { variant: 'error' });
          return;
        }

        // Validar duración mínima
        const fin = dayjs(`${data.fecha} ${data.horaFin}`);
        const duracion = fin.diff(inicio, 'minutes');
        if (duracion < 15) {
          enqueueSnackbar('La duración mínima es de 15 minutos', { variant: 'error' });
          return;
        }

        // Verificar disponibilidad
        const availability = await checkAvailability();
        if (!availability.available) {
          enqueueSnackbar(availability.message, { variant: 'error' });
          return;
        }

        const fechaInicioISO = inicio.toISOString();
        const fechaFinISO = fin.toISOString();

        // ⭐⭐ EXTRAER idStaff del participante staff_principal
        const staffPrincipalParticipante = data.participantes.find(
          (p) => p.rol === 'staff_principal',
        );
        const alumnoPrincipalParticipante = data.participantes.find(
          (p) => p.rol === 'alumno_principal',
        );

        if (isEditMode) {
          // ⭐⭐ MODO EDICIÓN ⭐⭐
          const estadoAnterior = initialValues.estado;
          const estadoNuevo = data.estado;
          const cambioARealizada = estadoAnterior !== 'realizada' && estadoNuevo === 'realizada';

          const updatePayload = {
            idStaff: staffPrincipalParticipante?.idUsuario || 0, // ⭐⭐ Extraer de participantes
            idAlumno: alumnoPrincipalParticipante?.idUsuario || undefined,
            idReservaOrigen: data.idReservaOrigen,
            idCentro: data.idCentro,
            tipo: data.tipo,
            tipoAsistentes: data.tipoAsistentes,
            canal: data.canal,
            fechaInicio: fechaInicioISO,
            fechaFin: fechaFinISO,
            estado: data.estado,
            comentariosInternos: data.comentariosInternos,
            comentariosPublicos: data.comentariosPublicos,
            origen: data.origen,
            fechaEventoReferencia: data.fechaEventoReferencia,
            tipoEventoReferencia: data.tipoEventoReferencia,
            participantes: data.participantes.filter(
              (p) => p.rol !== 'staff_principal' && p.rol !== 'alumno_principal',
            ), // Solo participantes secundarios
          };

          const updatedReunion = await updateReunionMutation.mutateAsync({
            id: initialValues.id!,
            payload: updatePayload,
          });

          // Invalidar y refetch inmediatamente para actualizar el calendario
          await queryClient.invalidateQueries({ queryKey: ['reuniones'] });
          await queryClient.refetchQueries({ queryKey: ['reuniones'] });

          enqueueSnackbar('Reunión actualizada exitosamente', { variant: 'success' });

          // Si se cambió a "realizada", verificar si es de Apoyo/Counseling y abrir modal
          if (cambioARealizada) {
            try {
              // Obtener la reunión actualizada con información completa
              const reunionCompleta = await api.get(`/reuniones/${initialValues.id}`);
              const reunionData = reunionCompleta.data;

              // Verificar si es de Apoyo/Counseling
              if (esReunionApoyoOCounseling(reunionData)) {
                // Llamar al endpoint de estado para obtener información de siguiente reunión
                // Nota: El estado ya está actualizado, pero este endpoint retorna información adicional
                const estadoResponse = await api.patch(`/reuniones/${initialValues.id}/estado`, {
                  estado: 'realizada',
                });

                const estadoData = estadoResponse.data;

                // Si puede agendar siguiente, abrir modal
                if (estadoData?.puedeAgendarSiguiente && estadoData?.fechaSugeridaSiguiente) {
                  setModalSiguienteReunion({
                    open: true,
                    reunion: reunionData,
                    fechaSugerida: estadoData.fechaSugeridaSiguiente,
                  });
                }
              }
            } catch (error) {
              // Silenciar error, no es crítico si no se puede abrir el modal
            }
          }
        } else if (data.autoSchedule) {
          // ⭐⭐ AUTO-AGENDAMIENTO (CREACIÓN) ⭐⭐
          const autoPayload = {
            idAlumno: alumnoPrincipalParticipante?.idUsuario || 0,
            idReservaOrigen: data.idReservaOrigen,
            idCentro: data.idCentro,
            tipoReunion: data.tipoReunion,
            rolStaffDestino: data.rolStaffDestino,
            canal: data.canal,
            fechaEventoReferencia: data.fechaEventoReferencia || dayjs().toISOString(),
            tipoEventoReferencia: data.tipoEventoReferencia,
            fechaInicioMin: data.fecha ? dayjs(`${data.fecha} 00:00`).toISOString() : undefined,
            fechaInicioMax: data.fecha ? dayjs(`${data.fecha} 23:59`).toISOString() : undefined,
            duracionMinutos: duracion,
            idStaffFijo: data.idStaffFijo,
          };

          await createReunionAutoMutation.mutateAsync(autoPayload as any);
          
          // Invalidar y refetch inmediatamente para actualizar el calendario
          await queryClient.invalidateQueries({ queryKey: ['reuniones'] });
          await queryClient.refetchQueries({ queryKey: ['reuniones'] });
          
          enqueueSnackbar('Reunión creada exitosamente', { variant: 'success' });
        } else {
          // ⭐⭐ CREACIÓN MANUAL ⭐⭐
          const manualPayload = {
            idStaff: staffPrincipalParticipante?.idUsuario || 0, // ⭐⭐ Extraer de participantes
            idAlumno: alumnoPrincipalParticipante?.idUsuario || null,
            idReservaOrigen: data.idReservaOrigen,
            idCentro: data.idCentro,
            tipo: data.tipo,
            tipoAsistentes: data.tipoAsistentes,
            canal: data.canal,
            fechaInicio: fechaInicioISO,
            fechaFin: fechaFinISO,
            estado: data.estado,
            comentariosInternos: data.comentariosInternos,
            comentariosPublicos: data.comentariosPublicos,
            origen: data.origen,
            fechaEventoReferencia: data.fechaEventoReferencia,
            tipoEventoReferencia: data.tipoEventoReferencia,
            participantes: data.participantes.filter(
              (p) => p.rol !== 'staff_principal' && p.rol !== 'alumno_principal',
            ), // Solo participantes secundarios
          };

          await createReunionMutation.mutateAsync(manualPayload as any);
          
          // Invalidar y refetch inmediatamente para actualizar el calendario
          await queryClient.invalidateQueries({ queryKey: ['reuniones'] });
          await queryClient.refetchQueries({ queryKey: ['reuniones'] });
          
          enqueueSnackbar('Reunión creada exitosamente', { variant: 'success' });
        }

        onSaved?.();
      } catch (error) {
        console.error('Error al guardar reunión:', error);
        let message = isEditMode ? 'Error al actualizar la reunión' : 'Error al crear la reunión';

        if (isAxiosError(error)) {
          message = error.response?.data?.message || message;
          if (error.response?.data?.details) {
            message += `: ${JSON.stringify(error.response.data.details)}`;
          }
          // Mostrar más detalles del error
          if (error.response?.status) {
            console.error('Status del error:', error.response.status);
            console.error('Datos del error:', error.response.data);
          }
        } else {
          console.error('Error no es AxiosError:', error);
        }

        enqueueSnackbar(message, { variant: 'error' });
      }
    },
    [
      isEditMode,
      initialValues.id,
      updateReunionMutation,
      createReunionAutoMutation,
      createReunionMutation,
      enqueueSnackbar,
      onSaved,
      queryClient,
      checkAvailability,
    ],
  );

  /* ── Agregar participante ── */
  const handleAddParticipant = useCallback(
    (rol: ReunionParticipanteRol = 'staff_secundario') => {
      append({
        idUsuario: 0,
        rol,
        estadoParticipacion: 'pendiente' as const,
        requerido: true,
        notas: '',
      });
    },
    [append],
  );

  /* ── Establecer staff principal ── */
  const handleSetStaffPrincipal = useCallback(
    (usuario: any) => {
      // Buscar si ya existe un staff principal
      const index = participantes.findIndex((p) => p.rol === 'staff_principal');

      if (index >= 0) {
        // Actualizar existente
        update(index, {
          ...participantes[index],
          idUsuario: usuario.id,
        });
      } else {
        // Agregar nuevo al inicio
        append(
          {
            idUsuario: usuario.id,
            rol: 'staff_principal' as const,
            estadoParticipacion: 'pendiente' as const,
            requerido: true,
            notas: '',
          },
          { shouldFocus: false },
        );
      }
    },
    [participantes, update, append],
  );

  /* ── Establecer alumno principal ── */
  const handleSetAlumnoPrincipal = useCallback(
    (usuario: any) => {
      // Buscar si ya existe un alumno principal
      const index = participantes.findIndex((p) => p.rol === 'alumno_principal');

      if (index >= 0) {
        // Actualizar existente
        update(index, {
          ...participantes[index],
          idUsuario: usuario.id,
        });
      } else {
        // Agregar nuevo
        append(
          {
            idUsuario: usuario.id,
            rol: 'alumno_principal' as const,
            estadoParticipacion: 'pendiente' as const,
            requerido: true,
            notas: '',
          },
          { shouldFocus: false },
        );
      }
    },
    [participantes, update, append],
  );

  /* ── Obtener usuario por ID ── */
  const getUsuarioById = useCallback(
    (id: number) => {
      return usuarios.find((u: any) => u.id === id);
    },
    [usuarios],
  );

  /* ── Obtener alumno por ID ── */
  const getAlumnoById = useCallback(
    (id: number) => {
      return alumnosList.find((a: any) => a.id === id);
    },
    [alumnosList],
  );

  /* ── Validar errores de participantes ── */
  const participantesErrors = errors.participantes;
  const participantesRootError = errors.participantes?.message;

  /* ── Debug: mostrar errores de validación ── */
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('Errores de validación:', errors);
    }
  }, [errors]);

  /* ── Mostrar loading ── */
  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando datos...</Typography>
      </Stack>
    );
  }

  /* ── Render ── */

  return (
    <form
      onSubmit={handleSubmit(
        onSubmit,
        (validationErrors) => {
          console.log('Errores de validación al enviar:', validationErrors);
          enqueueSnackbar('Por favor, corrige los errores en el formulario', { variant: 'error' });
        },
      )}
    >
      <Grid container spacing={2}>
        {/* --- Indicador de modo edición --- */}
        {isEditMode && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 1,
                backgroundColor: '#e3f2fd',
                borderRadius: 1,
                borderLeft: '4px solid #2196f3',
              }}
            >
              <Typography variant="subtitle2" color="primary">
                Editando reunión #{initialValues.id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                En modo edición, puedes modificar todos los campos excepto el origen
              </Typography>
            </Box>
          </Grid>
        )}

        {/* --- Auto-schedule toggle (solo en modo creación) --- */}
        {!isEditMode && (
          <Grid item xs={12}>
            <FormLabel component="legend">{t('reuniones.form.mode', 'Modo de creación')}</FormLabel>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {isAutoSchedule
                ? 'El sistema asignará automáticamente el staff según las reglas'
                : 'Selecciona manualmente todos los detalles'}
            </Typography>
          </Grid>
        )}

        {/* --- Staff fijo (auto - solo creación) --- */}
        {isAutoSchedule && !isEditMode && (
          <>
            <Grid item xs={12}>
              <Controller
                name="rolStaffDestino"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <InputLabel>Rol de staff destino *</InputLabel>
                    <Select {...field} label="Rol de staff destino *">
                      {REGLA_ROL_STAFF_DESTINO_VALUES.map((rol) => (
                        <MenuItem key={rol} value={rol}>
                          {rol === 'att_familias' && 'Atención a Familias'}
                          {rol === 'att_alumnado' && 'Atención al Alumnado'}
                          {rol === 'admisiones' && 'Admisiones'}
                          {rol === 'otro' && 'Otro'}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldState.error && (
                      <Typography variant="caption" color="error">
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* --- Tipo de reunión (auto) --- */}
            <Grid item xs={12}>
              <Controller
                name="tipoReunion"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth error={!!fieldState.error}>
                    <InputLabel>Tipo de reunión *</InputLabel>
                    <Select {...field} label="Tipo de reunión *">
                      {REUNION_TIPO_VALUES.map((tipo) => (
                        <MenuItem key={tipo} value={tipo}>
                          {tipo === 'bienvenida' && 'Bienvenida'}
                          {tipo === 'seguimiento' && 'Seguimiento'}
                          {tipo === 'incidencia' && 'Incidencia'}
                          {tipo === 'otro' && 'Otro'}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary">
                      Tipo de reunión para aplicar las reglas de agendación
                    </Typography>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="idStaffFijo"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Staff fijo (opcional)</InputLabel>
                    <Select
                      {...field}
                      label="Staff fijo (opcional)"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(Number(e.target.value) || null)}
                    >
                      <MenuItem value="">Asignación automática</MenuItem>
                      {staffList.map((staff: any) => (
                        <MenuItem key={staff.id} value={staff.id}>
                          {staff.nombreCompleto ??
                            ([staff.nombre, staff.apellidos].filter(Boolean).join(' ') ||
                              staff.email)}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary">
                      Si se especifica, se usará este staff en lugar de asignar automáticamente
                    </Typography>
                  </FormControl>
                )}
              />
            </Grid>
          </>
        )}

        {/* --- Sección de participantes (solo modo manual) --- */}
        {!isAutoSchedule && (
          <Grid item xs={12}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                <Tab label="Participantes principales" icon={<Person />} iconPosition="start" />
                <Tab label="Participantes secundarios" icon={<Group />} iconPosition="start" />
                <Tab label="Detalles de la reunión" icon={<Schedule />} iconPosition="start" />
              </Tabs>
            </Box>

            {/* TAB 1: Participantes principales */}
            {activeTab === 0 && (
              <Stack spacing={3}>
                {/* Errores generales de participantes */}
                {participantesRootError && <Alert severity="error">{participantesRootError}</Alert>}

                {/* Staff principal */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Staff principal *
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    El staff principal es quien coordina y lidera la reunión. Solo puede haber uno.
                  </Alert>

                  <Autocomplete
                    options={staffList}
                    getOptionLabel={(option: any) =>
                      option.nombreCompleto ??
                      ([option.nombre, option.apellidos, option.email].filter(Boolean).join(' ') ||
                        option.email)
                    }
                    value={staffPrincipal ? getUsuarioById(staffPrincipal.idUsuario) : null}
                    onChange={(_, value: any | null) => {
                      if (value) {
                        handleSetStaffPrincipal(value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleccionar staff principal"
                        placeholder="Buscar staff..."
                        error={!!participantesErrors?.root?.message?.includes('staff principal')}
                        helperText={
                          participantesErrors?.root?.message?.includes('staff principal')
                            ? participantesErrors.root.message
                            : ''
                        }
                      />
                    )}
                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                  />

                  {staffPrincipal && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>Staff principal seleccionado:</strong>{' '}
                        {getUsuarioById(staffPrincipal.idUsuario)?.nombreCompleto ||
                          getUsuarioById(staffPrincipal.idUsuario)?.nombreCompleto ||
                          `ID: ${staffPrincipal.idUsuario}`}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Alumno principal */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Alumno principal (opcional)
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    El alumno principal es el cliente/familia con quien se realizará la reunión.
                  </Alert>

                  <Autocomplete
                    options={alumnosList as any[]}
                    getOptionLabel={(option: any) =>
                      option.nombreCompleto
                        ? `${option.nombreCompleto} (${option.email ?? ''})`
                        : [option.nombre, option.apellidos, option.email].filter(Boolean).join(' ')
                    }
                    value={alumnoPrincipal ? getAlumnoById(alumnoPrincipal.idUsuario) : null}
                    onChange={(_, value: any | null) => {
                      if (value) {
                        handleSetAlumnoPrincipal(value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleccionar alumno principal"
                        placeholder="Buscar alumno..."
                        error={!!participantesErrors?.root?.message?.includes('alumno principal')}
                        helperText={
                          participantesErrors?.root?.message?.includes('alumno principal')
                            ? participantesErrors.root.message
                            : ''
                        }
                      />
                    )}
                    isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                  />

                  {alumnoPrincipal && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>Alumno principal seleccionado:</strong>{' '}
                        {getAlumnoById(alumnoPrincipal.idUsuario)?.nombreCompleto ||
                          getAlumnoById(alumnoPrincipal.idUsuario)?.nombreCompleto ||
                          `ID: ${alumnoPrincipal.idUsuario}`}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Resumen de participantes principales */}
                {(staffPrincipal || alumnoPrincipal) && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Resumen de participantes principales:
                    </Typography>
                    <Stack spacing={1}>
                      {staffPrincipal && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            👨 💼 <strong>Staff principal:</strong>{' '}
                            {getUsuarioById(staffPrincipal.idUsuario)?.nombreCompleto ||
                              `ID: ${staffPrincipal.idUsuario}`}
                          </Typography>
                          <Chip label="Requerido" color="primary" size="small" />
                        </Box>
                      )}
                      {alumnoPrincipal && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">
                            👨 🎓 <strong>Alumno principal:</strong>{' '}
                            {getAlumnoById(alumnoPrincipal.idUsuario)?.nombreCompleto ||
                              `ID: ${alumnoPrincipal.idUsuario}`}
                          </Typography>
                          <Chip label="Requerido" color="secondary" size="small" />
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}
              </Stack>
            )}

            {/* TAB 2: Participantes secundarios */}
            {activeTab === 1 && (
              <Stack spacing={3}>
                <Typography variant="h6" gutterBottom>
                  Participantes secundarios
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Agrega staff secundario, alumnos secundarios o invitados a la reunión.
                </Alert>

                {/* Botones para agregar rápidamente */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    startIcon={<PersonAdd />}
                    onClick={() => handleAddParticipant('staff_secundario')}
                    variant="outlined"
                    size="small"
                  >
                    Agregar staff secundario
                  </Button>
                  <Button
                    startIcon={<PersonAdd />}
                    onClick={() => handleAddParticipant('alumno_secundario')}
                    variant="outlined"
                    size="small"
                  >
                    Agregar alumno secundario
                  </Button>
                  <Button
                    startIcon={<PersonAdd />}
                    onClick={() => handleAddParticipant('invitado')}
                    variant="outlined"
                    size="small"
                  >
                    Agregar invitado
                  </Button>
                </Box>

                {/* Tabla de participantes secundarios */}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="30%">Usuario</TableCell>
                        <TableCell width="15%">Rol</TableCell>
                        <TableCell width="15%">Estado</TableCell>
                        <TableCell width="10%">Requerido</TableCell>
                        <TableCell width="20%">Notas</TableCell>
                        <TableCell width="10%" align="right">
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((field, index) => {
                        const participante = participantes[index];
                        // Solo mostrar si es secundario
                        if (
                          participante.rol === 'staff_principal' ||
                          participante.rol === 'alumno_principal'
                        ) {
                          return null;
                        }

                        // Determinar qué lista de usuarios usar según el rol
                        let opcionesUsuarios: any[] = [];
                        switch (participante.rol) {
                          case 'staff_secundario':
                            opcionesUsuarios = staffList; // Usar la misma lista filtrada que para staff principal
                            break;
                          case 'alumno_secundario':
                            opcionesUsuarios = alumnosList; // Usar lista de alumnos
                            break;
                          case 'invitado':
                            opcionesUsuarios = usuarios; // Usar lista completa para invitados
                            break;
                          default:
                            opcionesUsuarios = usuarios;
                        }

                        return (
                          <TableRow key={field.id}>
                            <TableCell>
                              <Controller
                                name={`participantes.${index}.idUsuario`}
                                control={control}
                                render={({ field: fieldCtrl }) => (
                                  <Autocomplete
                                    options={opcionesUsuarios}
                                    getOptionLabel={(option: any) => {
                                      // Formatear según el tipo de usuario
                                      if (participante.rol === 'alumno_secundario') {
                                        // Formato para alumnos
                                        return option.nombreCompleto
                                          ? `${option.nombreCompleto} (${option.email ?? ''})`
                                          : [option.nombre, option.apellidos, option.email]
                                              .filter(Boolean)
                                              .join(' ');
                                      } else {
                                        // Formato para staff e invitados
                                        return (
                                          option.nombreCompleto ??
                                          [option.nombre, option.apellidos, option.email]
                                            .filter(Boolean)
                                            .join(' ')
                                        );
                                      }
                                    }}
                                    value={
                                      opcionesUsuarios.find((u: any) => u.id === fieldCtrl.value) ||
                                      null
                                    }
                                    onChange={(_, value) => fieldCtrl.onChange(value?.id || 0)}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        placeholder={
                                          participante.rol === 'staff_secundario'
                                            ? 'Seleccionar staff...'
                                            : participante.rol === 'alumno_secundario'
                                              ? 'Seleccionar alumno...'
                                              : 'Seleccionar usuario...'
                                        }
                                        error={!!errors.participantes?.[index]?.idUsuario}
                                        helperText={
                                          errors.participantes?.[index]?.idUsuario?.message
                                        }
                                      />
                                    )}
                                    isOptionEqualToValue={(option: any, value: any) =>
                                      option.id === value.id
                                    }
                                    // Resetear el valor si el rol cambia y el usuario no es válido para el nuevo rol
                                    key={`autocomplete-${index}-${participante.rol}`}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`participantes.${index}.rol`}
                                control={control}
                                render={({ field: fieldCtrl }) => (
                                  <Select
                                    {...fieldCtrl}
                                    size="small"
                                    fullWidth
                                    onChange={(e) => {
                                      // Resetear el usuario cuando cambia el rol
                                      setValue(`participantes.${index}.idUsuario`, 0);
                                      fieldCtrl.onChange(e);
                                    }}
                                  >
                                    {REUNION_PARTICIPANTE_ROL_VALUES.filter(
                                      (rol) =>
                                        rol !== 'staff_principal' && rol !== 'alumno_principal',
                                    ).map((rol) => (
                                      <MenuItem key={rol} value={rol}>
                                        {rol === 'staff_secundario' && 'Staff Secundario'}
                                        {rol === 'alumno_secundario' && 'Alumno Secundario'}
                                        {rol === 'invitado' && 'Invitado'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`participantes.${index}.estadoParticipacion`}
                                control={control}
                                render={({ field: fieldCtrl }) => (
                                  <Select {...fieldCtrl} size="small" fullWidth>
                                    {REUNION_PARTICIPANTE_ESTADO_VALUES.map((estado) => (
                                      <MenuItem key={estado} value={estado}>
                                        {estado === 'pendiente' && 'Pendiente'}
                                        {estado === 'confirmado' && 'Confirmado'}
                                        {estado === 'rechazado' && 'Rechazado'}
                                        {estado === 'cancelado' && 'Cancelado'}
                                        {estado === 'asistio' && 'Asistió'}
                                        {estado === 'no_asistio' && 'No Asistió'}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`participantes.${index}.requerido`}
                                control={control}
                                render={({ field: fieldCtrl }) => (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={fieldCtrl.value}
                                        onChange={(e) => fieldCtrl.onChange(e.target.checked)}
                                        size="small"
                                      />
                                    }
                                    label={fieldCtrl.value ? 'Sí' : 'No'}
                                    sx={{ m: 0 }}
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Controller
                                name={`participantes.${index}.notas`}
                                control={control}
                                render={({ field: fieldCtrl }) => (
                                  <TextField
                                    {...fieldCtrl}
                                    size="small"
                                    fullWidth
                                    placeholder="Notas..."
                                  />
                                )}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => remove(index)} color="error">
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {participantesSecundarios.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              No hay participantes secundarios
                            </Typography>
                            <Button
                              startIcon={<Add />}
                              onClick={() => handleAddParticipant('staff_secundario')}
                              sx={{ mt: 1 }}
                              size="small"
                            >
                              Agregar primer participante
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Resumen total */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Resumen total de participantes: {participantes.length}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    <Chip
                      label={`Staff principal: ${staffPrincipal ? '✓' : '✗'}`}
                      color={staffPrincipal ? 'success' : 'error'}
                      size="small"
                    />
                    <Chip
                      label={`Alumno principal: ${alumnoPrincipal ? '✓' : 'opcional'}`}
                      color={alumnoPrincipal ? 'success' : 'default'}
                      size="small"
                    />
                    <Chip
                      label={`Secundarios: ${participantesSecundarios.length}`}
                      color="info"
                      size="small"
                    />
                  </Box>
                </Box>
              </Stack>
            )}

            {/* TAB 3: Detalles de la reunión */}
            {activeTab === 2 && (
              <Stack spacing={3}>
                {/* Tipo de reunión */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="tipo"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl fullWidth error={!!fieldState.error}>
                          <InputLabel>Tipo de reunión *</InputLabel>
                          <Select {...field} label="Tipo de reunión *">
                            {REUNION_TIPO_VALUES.map((tipo) => (
                              <MenuItem key={tipo} value={tipo}>
                                {tipo === 'bienvenida' && 'Bienvenida'}
                                {tipo === 'seguimiento' && 'Seguimiento'}
                                {tipo === 'incidencia' && 'Incidencia'}
                                {tipo === 'otro' && 'Otro'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  {/* Tipo de asistentes */}
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="tipoAsistentes"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl fullWidth error={!!fieldState.error}>
                          <InputLabel>Tipo de asistentes *</InputLabel>
                          <Select {...field} label="Tipo de asistentes *">
                            {REUNION_TIPO_ASISTENTES_VALUES.map((tipo) => (
                              <MenuItem key={tipo} value={tipo}>
                                {tipo === 'solo_tutores' && t('reuniones.attendees.solo_tutores')}
                                {tipo === 'solo_alumno' && t('reuniones.attendees.solo_alumno')}
                                {tipo === 'tutores_y_alumno' && t('reuniones.attendees.tutores_y_alumno')}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Canal y Centro */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="canal"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl fullWidth error={!!fieldState.error}>
                          <InputLabel>Canal *</InputLabel>
                          <Select {...field} label="Canal *">
                            {REUNION_CANAL_VALUES.map((canal) => (
                              <MenuItem key={canal} value={canal}>
                                {canal === 'telefonico' && 'Telefónico'}
                                {canal === 'online' && 'Online'}
                                {canal === 'presencial' && 'Presencial'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="idCentro"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Centro </InputLabel>
                          <Select
                            {...field}
                            label="Centro"
                            value={field.value || ''}
                            onChange={(e) => field.onChange(Number(e.target.value) || null)}
                          >
                            <MenuItem value="">Sin centro</MenuItem>
                            {centrosList.map((centro: any) => (
                              <MenuItem key={centro.id} value={centro.id}>
                                {centro.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Origen y Estado */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="origen"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl fullWidth error={!!fieldState.error}>
                          <InputLabel>Origen</InputLabel>
                          <Select {...field} label="Origen" disabled={isEditMode}>
                            {REUNION_ORIGEN_VALUES.map((origen) => (
                              <MenuItem key={origen} value={origen}>
                                {origen === 'manual' && 'Manual'}
                                {origen === 'pasarela_ventas' && 'Pasarela de ventas'}
                                {origen === 'otro' && 'Otro'}
                              </MenuItem>
                            ))}
                          </Select>
                          {isEditMode && (
                            <Typography variant="caption" color="text.secondary">
                              El origen no se puede modificar
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="estado"
                      control={control}
                      render={({ field, fieldState }) => (
                        <FormControl fullWidth error={!!fieldState.error}>
                          <InputLabel>Estado</InputLabel>
                          <Select {...field} label="Estado">
                            {REUNION_ESTADO_VALUES.map((estado) => (
                              <MenuItem key={estado} value={estado}>
                                {estado === 'programada' && 'Programada'}
                                {estado === 'realizada' && 'Realizada'}
                                {estado === 'no_show_familia' && 'No Show Familia'}
                                {estado === 'no_show_staff' && 'No Show Staff'}
                                {estado === 'cancelada_familia' && 'Cancelada por Familia'}
                                {estado === 'cancelada_staff' && 'Cancelada por Staff'}
                                {estado === 'cancelada_sistema' && 'Cancelada por Sistema'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Fechas y horas */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="fecha"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type="date"
                          fullWidth
                          label="Fecha *"
                          InputLabelProps={{ shrink: true }}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          inputProps={{
                            min: dayjs().format('YYYY-MM-DD'),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Controller
                      name="horaInicio"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type="time"
                          fullWidth
                          label="Hora inicio *"
                          InputLabelProps={{ shrink: true }}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          inputProps={{ step: 300 }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Controller
                      name="horaFin"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          type="time"
                          fullWidth
                          label="Hora fin *"
                          InputLabelProps={{ shrink: true }}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                          inputProps={{ step: 300 }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Duración y validaciones */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      Duración: {duracionMinutos} minutos
                    </Typography>

                    {fechaInicioCompleta && (
                      <>
                        {fechaInicioCompleta.isBefore(dayjs()) && (
                          <Chip
                            label="En el pasado"
                            color="error"
                            size="small"
                            icon={<Schedule />}
                          />
                        )}
                        {duracionMinutos < 15 && (
                          <Chip label="Duración mínima: 15 min" color="warning" size="small" />
                        )}
                      </>
                    )}

                    {checkingAvailability && (
                      <Chip
                        label="Verificando disponibilidad..."
                        color="info"
                        size="small"
                        icon={<CircularProgress size={16} />}
                      />
                    )}
                  </Box>
                </Grid>

                {/* Comentarios */}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="comentariosPublicos"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={2}
                          label="Comentarios públicos"
                          placeholder="Información visible para el cliente (se incluirá en emails)..."
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="comentariosInternos"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={2}
                          label="Comentarios internos"
                          placeholder="Información solo para staff..."
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Evento de referencia */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="fechaEventoReferencia"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="datetime-local"
                          fullWidth
                          label="Fecha evento referencia"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="tipoEventoReferencia"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>Tipo evento referencia</InputLabel>
                          <Select {...field} label="Tipo evento referencia">
                            <MenuItem value="">Sin tipo</MenuItem>
                            {REUNION_TIPO_EVENTO_REFERENCIA_VALUES.map((tipo) => (
                              <MenuItem key={tipo} value={tipo}>
                                {tipo === 'nueva_matricula' && 'Nueva matrícula'}
                                {tipo === 'incidencia' && 'Incidencia'}
                                {tipo === 'otro' && 'Otro'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Reserva origen */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="idReservaOrigen"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          options={reservasList}
                          getOptionLabel={(option) =>
                            `Reserva #${option.id} - ${option.asignatura?.nombre || 'Sin asignatura'}`
                          }
                          value={reservasList.find((reserva) => reserva.id === field.value) || null}
                          onChange={(_, value) => field.onChange(value?.id || null)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Reserva origen (opcional)"
                              placeholder="Buscar reserva..."
                            />
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Stack>
            )}

            {/* Navegación entre tabs */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                onClick={() => setActiveTab((prev) => Math.max(0, prev - 1))}
                disabled={activeTab === 0}
                variant="outlined"
              >
                Anterior
              </Button>

              {activeTab < 2 ? (
                <Button
                  onClick={() => setActiveTab((prev) => Math.min(2, prev + 1))}
                  variant="contained"
                >
                  Siguiente
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button onClick={() => setActiveTab(0)} variant="outlined">
                    Revisar participantes
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        )}

        {/* --- Modo auto: Campos básicos --- */}
        {isAutoSchedule && !isEditMode && (
          <Grid container spacing={2}>
            {/* Alumno principal para auto */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Alumno principal *
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Selecciona el alumno con quien se realizará la reunión
              </Alert>

              <Controller
                name="participantes"
                control={control}
                render={({ field }) => {
                  // En modo auto, solo manejamos alumno principal
                  const alumnoId = field.value?.find(
                    (p) => p.rol === 'alumno_principal',
                  )?.idUsuario;

                  return (
                    <Autocomplete
                      options={alumnosList as any[]}
                      getOptionLabel={(option: any) =>
                        option.nombreCompleto
                          ? `${option.nombreCompleto} (${option.email ?? ''})`
                          : [option.nombre, option.apellidos, option.email]
                              .filter(Boolean)
                              .join(' ')
                      }
                      value={alumnoId ? getAlumnoById(alumnoId) : null}
                      onChange={(_, value: any | null) => {
                        const nuevosParticipantes = [
                          {
                            idUsuario: value?.id || 0,
                            rol: 'alumno_principal' as const,
                            estadoParticipacion: 'pendiente' as const,
                            requerido: true,
                            notas: '',
                          },
                        ];
                        field.onChange(nuevosParticipantes);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seleccionar alumno principal"
                          placeholder="Buscar alumno..."
                          error={!!errors.participantes}
                          helperText={errors.participantes?.message}
                          required
                        />
                      )}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                    />
                  );
                }}
              />
            </Grid>

            {/* Centro preferente para auto */}
            <Grid item xs={12}>
              <Controller
                name="idCentro"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Centro</InputLabel>
                    <Select
                      {...field}
                      label="Centro preferente (opcional)"
                      value={field.value || ''}
                      onChange={(e) => field.onChange(Number(e.target.value) || null)}
                    >
                      <MenuItem value="">Sin preferencia</MenuItem>
                      {centrosList.map((centro: any) => (
                        <MenuItem key={centro.id} value={centro.id}>
                          {centro.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Campos de fecha/hora para auto (opcionales para ventana) */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="fecha"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="date"
                      fullWidth
                      label="Fecha preferente (opcional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message || 'Si no se especifica, se usará la fecha actual'
                      }
                      inputProps={{
                        min: dayjs().format('YYYY-MM-DD'),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Controller
                  name="horaInicio"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="time"
                      fullWidth
                      label="Hora inicio (opcional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      inputProps={{ step: 300 }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Controller
                  name="horaFin"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="time"
                      fullWidth
                      label="Hora fin (opcional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      inputProps={{ step: 300 }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Duración */}
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Duración estimada:{' '}
                {duracionMinutos > 0
                  ? `${duracionMinutos} minutos`
                  : 'Se usará la duración de la regla'}
              </Typography>
            </Grid>

            {/* Comentarios para auto */}
            <Grid item xs={12}>
              <Controller
                name="comentariosPublicos"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={2}
                    label="Comentarios públicos (opcional)"
                    placeholder="Información visible para el cliente..."
                  />
                )}
              />
            </Grid>
          </Grid>
        )}

        {/* --- Botones finales --- */}
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onCancel} disabled={isSubmitting} variant="outlined">
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || checkingAvailability}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              sx={{
                backgroundColor: '#21a598',
                '&:hover': { backgroundColor: '#1a8578' },
                '&:disabled': { backgroundColor: '#cccccc' },
              }}
            >
              {isSubmitting
                ? 'Guardando...'
                : isEditMode
                  ? t('common.update', 'Actualizar')
                  : t('common.save', 'Guardar')}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Modal para agendar siguiente reunión */}
      <AgendarSiguienteReunionModal
        open={modalSiguienteReunion.open}
        reunion={modalSiguienteReunion.reunion}
        fechaSugerida={modalSiguienteReunion.fechaSugerida}
        onClose={() => {
          setModalSiguienteReunion({ open: false, reunion: null, fechaSugerida: null });
        }}
        onSuccess={(nuevaReunion: Reunion) => {
          // Invalidar queries para refrescar el calendario
          queryClient.invalidateQueries({ queryKey: ['reuniones'] });
          enqueueSnackbar('Siguiente reunión agendada correctamente', { variant: 'success' });
        }}
      />
    </form>
  );
}
